import './styles/index.scss'; // style import should be the first

import { FC, PropsWithChildren, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import SendbirdProvider from '@sendbird/uikit-react/SendbirdProvider';
import { BrowserRouter as Router } from 'react-router-dom';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { ConfigProvider } from 'antd';

import store from './store';
import { App } from './App';
import {
  getAffiliateGlobalStylesSelector,
  getAppThemeSelector,
  getWhiteLabelAffiliateSelector
} from 'store/app/app.redux';

import './i18n';
import { antLanguage } from 'shared/utils/ant-language';
import { LANGUAGE, getItemFromLocalStorage } from 'shared/utils/local-storage';
import IntegrationsProvider from 'shared/utils/integrations';
import {
  getChannels,
  getPatientConversationChannelsSelector
} from 'store/patientConversation/patientConversation.redux';
import '@sendbird/uikit-react/dist/index.css';
import { getUserSelector } from 'store/user/user.redux';

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ''
);

const OPTIONS = {
  fonts: [
    {
      cssSrc: 'https://fonts.googleapis.com/css2?family=Oswald&display=swap'
    }
  ]
};

const AppWrapper: FC<PropsWithChildren> = () => {
  const appTheme = useSelector(getAppThemeSelector);
  const affiliateGlobalStyles = useSelector(getAffiliateGlobalStylesSelector);
  const dispatch = useDispatch();
  const language = getItemFromLocalStorage(LANGUAGE);
  const user = useSelector(getUserSelector);
  const patientConversation = useSelector(
    getPatientConversationChannelsSelector
  );

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data === 'clearLocalStorage') {
        localStorage.clear();
        sessionStorage.clear(); // Also clear sessionStorage to remove tokens
        console.log('OVS local storage has been cleared.');
      }
    };

    window.addEventListener('message', handleMessage);

    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  useEffect(() => {
    if (user) {
      dispatch(getChannels({ patientId: user?.patient?.id }));
    }
  }, [dispatch, getChannels, user]);

  return (
    <div className="appWrapper" style={affiliateGlobalStyles?.variables}>
      <ConfigProvider theme={appTheme} locale={antLanguage[language]}>
        <Elements stripe={stripePromise} options={OPTIONS}>
          <Router>
            <IntegrationsProvider>
              {patientConversation ? (
                <SendbirdProvider
                  appId={process.env.REACT_APP_SENDBIRD_APP_ID || ''}
                  userId={patientConversation?.participantIdentifier || ''}
                  accessToken={
                    patientConversation?.participantSecondaryIdentifier
                  }
                >
                  <App />
                </SendbirdProvider>
              ) : (
                <App />
              )}
            </IntegrationsProvider>
          </Router>
        </Elements>
      </ConfigProvider>
    </div>
  );
};

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <Provider store={store}>
    <AppWrapper />
  </Provider>
);
